import { useState, useCallback, useRef } from "react";
import { GetAllRepliesService } from "shared/services/reply";
import { handleErrors } from "shared/utils/helper";
import { Message } from "shared/types/chatBoard";
import { sortRepliesChronologically } from "shared/utils/chatBoardUtils";

interface UseChatBoardRepliesProps {
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
}

export default function useChatBoardReplies({ setMessages }: UseChatBoardRepliesProps) {
  const [replies, setReplies] = useState<{ [commentId: string]: any[] }>({});
  const [loadingReplies, setLoadingReplies] = useState<{ [commentId: string]: boolean }>({});
  const fetchingRef = useRef<Set<string>>(new Set());
  const loadedRef = useRef<Set<string>>(new Set());

  const fetchReplies = useCallback(async (commentId: string) => {
    if (fetchingRef.current.has(commentId) || loadedRef.current.has(commentId)) {
      return;
    }

    fetchingRef.current.add(commentId);
    setLoadingReplies((prev) => ({ ...prev, [commentId]: true }));

    try {
      const response = await GetAllRepliesService(commentId);

      if (response.status === 200) {
        let apiReplies: any[] = [];

        if (Array.isArray(response.data)) {
          apiReplies = response.data;
        } else if (Array.isArray(response.data?.data)) {
          apiReplies = response.data.data;
        } else if (Array.isArray(response.data?.replies)) {
          apiReplies = response.data.replies;
        }

        setReplies((prev) => ({
          ...prev,
          [commentId]: sortRepliesChronologically(apiReplies || []),
        }));

        loadedRef.current.add(commentId);

        if (apiReplies && apiReplies.length > 0) {
          const sortedReplies = [...apiReplies].sort((a, b) => {
            const dateA = new Date(a.created_at || a.createdAt || 0).getTime();
            const dateB = new Date(b.created_at || b.createdAt || 0).getTime();
            return dateB - dateA;
          });

          const mostRecentReply = sortedReplies[0];
          const lastReplyAt = mostRecentReply.created_at || mostRecentReply.createdAt;

          if (lastReplyAt) {
            setMessages((prev) => {
              const messageIndex = prev.findIndex((msg) => msg.id.toString() === commentId);
              if (messageIndex !== -1) {
                const updatedMessages = [...prev];
                updatedMessages[messageIndex] = {
                  ...updatedMessages[messageIndex],
                  lastReplyAt: lastReplyAt,
                };
                return updatedMessages;
              }
              return prev;
            });
          }
        }
      }
    } catch (error: any) {
      handleErrors(error);
    } finally {
      fetchingRef.current.delete(commentId);
      setLoadingReplies((prev) => ({ ...prev, [commentId]: false }));
    }
  }, [setMessages]);

  return {
    replies,
    setReplies,
    loadingReplies,
    fetchReplies,
  };
}
